import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, Database, Building2, Package,
  Upload, Users, Target, Briefcase, RefreshCw,
  CheckCircle2, Circle, ChevronRight, AlertCircle,
  FileSpreadsheet, Link, Loader2, Clock, XCircle, CheckCircle,
} from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';

/**
 * AdminHub — full-screen admin home (admin role only).
 *
 * The admin's primary job is setup and operations, not field sales:
 * import data, configure the org, manage services. This hub puts those
 * front and center instead of burying them at the bottom of the nav rail.
 *
 * Tabs:
 *   Overview — onboarding checklist + org health stats
 *   Data     — import history + launch the import wizard
 *   Settings — context card that opens the existing OrgSettings modal
 *   Services — context card that opens the existing OrgServices modal
 *
 * Props:
 *   currentUser        — logged-in user (must be role 'admin')
 *   onOpenImport       — opens DataImportModal
 *   onOpenOrgSettings  — opens OrgSettings modal
 *   onOpenOrgServices  — opens OrgServices modal
 *   refreshKey         — bump to re-fetch (e.g. after an import completes)
 */

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'data', label: 'Data', icon: Database },
  { id: 'settings', label: 'Org Settings', icon: Building2 },
  { id: 'services', label: 'Services', icon: Package },
];

const BATCH_STATUS_STYLES = {
  completed: 'bg-green-50 text-green-700 border-green-200',
  processing: 'bg-blue-50 text-blue-700 border-blue-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  pending: 'bg-gray-50 text-gray-600 border-gray-200',
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function AdminHub({
  currentUser,
  onOpenImport,
  onOpenOrgSettings,
  onOpenOrgServices,
  refreshKey = 0,
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Checklist steps 2 & 3 have no server-side "configured" signal, so we
  // track first visits locally — good enough for an onboarding nudge.
  const [settingsVisited, setSettingsVisited] = useState(
    () => localStorage.getItem('admin_hub_settings_visited') === '1'
  );
  const [servicesVisited, setServicesVisited] = useState(
    () => localStorage.getItem('admin_hub_services_visited') === '1'
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ovRes, batchRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/overview`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/api/import/batches`, { withCredentials: true }),
      ]);
      setOverview(ovRes.data);
      setBatches(batchRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load admin data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const openSettings = () => {
    try { localStorage.setItem('admin_hub_settings_visited', '1'); } catch {}
    setSettingsVisited(true);
    onOpenOrgSettings();
  };

  const openServices = () => {
    try { localStorage.setItem('admin_hub_services_visited', '1'); } catch {}
    setServicesVisited(true);
    onOpenOrgServices();
  };

  const hasData = ((overview?.total_leads || 0) + (overview?.total_customers || 0)) > 0;

  const checklist = [
    {
      id: 'import',
      title: 'Import your leads & customers',
      desc: 'Bring in your prospect and client lists from Excel, CSV, or Google Sheets. NorthAI needs this data for AI briefings, scoring, and visit planning.',
      done: hasData,
      action: onOpenImport,
      actionLabel: 'Import data',
    },
    {
      id: 'settings',
      title: 'Review scoring & business rules',
      desc: 'Set how the AI prioritizes visits: scoring weights, distance bands, follow-up cadence. Sensible defaults are active until you change them.',
      done: settingsVisited,
      action: openSettings,
      actionLabel: 'Open settings',
    },
    {
      id: 'services',
      title: 'Check your services & plan',
      desc: 'Confirm which NorthAI services are active for your organization, including add-ons like Lead Concierge.',
      done: servicesVisited,
      action: openServices,
      actionLabel: 'View services',
    },
  ];

  const doneCount = checklist.filter(s => s.done).length;
  const allDone = doneCount === checklist.length;

  return (
    <div className="h-full overflow-y-auto bg-gray-50" data-testid="admin-hub">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">

        {/* ── Header ── */}
        <div className="mb-5">
          <h1 className="text-xl font-bold text-gray-900">Admin Hub</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Set up and manage {currentUser?.org_name || 'your organization'} — data, rules, and services.
          </p>
        </div>

        {/* ── Tabs ── */}
        <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors
                  ${active
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'}`}
              >
                <Icon size={16} /> {tab.label}
              </button>
            );
          })}
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 mb-5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="text-blue-500 animate-spin" />
          </div>
        ) : (
          <>
            {/* ════ OVERVIEW ════ */}
            {activeTab === 'overview' && (
              <div className="space-y-6">

                {/* Onboarding checklist — collapses to a slim banner when complete */}
                {allDone ? (
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <CheckCircle2 size={20} className="text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-800">
                      <span className="font-semibold">Setup complete.</span> Your organization is ready — your team can start using the map and AI briefings.
                    </p>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-base font-semibold text-gray-900">Get your team started</h2>
                        <p className="text-xs text-gray-500 mt-0.5">{doneCount} of {checklist.length} steps done</p>
                      </div>
                      <div className="flex gap-1">
                        {checklist.map(s => (
                          <div key={s.id} className={`w-8 h-1.5 rounded-full ${s.done ? 'bg-green-500' : 'bg-gray-200'}`} />
                        ))}
                      </div>
                    </div>
                    <div className="space-y-3">
                      {checklist.map(step => (
                        <div
                          key={step.id}
                          className={`flex items-start gap-3 p-3 rounded-lg border ${step.done ? 'border-green-100 bg-green-50/50' : 'border-gray-100 bg-gray-50'}`}
                        >
                          {step.done
                            ? <CheckCircle2 size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                            : <Circle size={20} className="text-gray-300 flex-shrink-0 mt-0.5" />}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${step.done ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                              {step.title}
                            </p>
                            {!step.done && <p className="text-xs text-gray-500 mt-1">{step.desc}</p>}
                          </div>
                          {!step.done && (
                            <button
                              onClick={step.action}
                              className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
                            >
                              {step.actionLabel} <ChevronRight size={13} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stat cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <StatCard icon={Target} label="Leads" value={overview?.total_leads ?? 0} color="blue" />
                  <StatCard icon={Briefcase} label="Customers" value={overview?.total_customers ?? 0} color="purple" />
                  <StatCard icon={Users} label="Team members" value={overview?.total_users ?? 0} color="green" />
                  <StatCard icon={Database} label="Imports run" value={overview?.total_imports ?? 0} color="amber" />
                </div>

                {/* Last import summary */}
                {overview?.last_import && (
                  <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-gray-100 rounded-lg flex-shrink-0">
                        <Clock size={16} className="text-gray-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900 truncate">
                          Last import: <span className="font-medium">{overview.last_import.filename || '—'}</span>
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(overview.last_import.created_at)} · {overview.last_import.successful_rows?.toLocaleString()} rows added
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setActiveTab('data')}
                      className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      View history
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ════ DATA ════ */}
            {activeTab === 'data' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">Data imports</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Import leads from Excel, CSV, or Google Sheets. Max 10 MB / 5,000 rows per file.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={fetchData}
                      title="Refresh"
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <RefreshCw size={16} />
                    </button>
                    <button
                      onClick={onOpenImport}
                      data-testid="admin-new-import"
                      className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                      <Upload size={15} /> New Import
                    </button>
                  </div>
                </div>

                {batches.length === 0 ? (
                  <div className="bg-white border-2 border-dashed border-gray-200 rounded-xl p-10 text-center">
                    <div className="inline-flex p-3 bg-gray-100 rounded-full mb-3">
                      <FileSpreadsheet size={24} className="text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">No imports yet</p>
                    <p className="text-xs text-gray-400 mt-1 mb-4">
                      Your map stays empty until you bring in your leads and customers.
                    </p>
                    <button
                      onClick={onOpenImport}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                      <Upload size={15} /> Import your first file
                    </button>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left px-4 py-2.5 font-medium text-gray-600">File</th>
                          <th className="text-left px-4 py-2.5 font-medium text-gray-600 hidden sm:table-cell">Source</th>
                          <th className="text-left px-4 py-2.5 font-medium text-gray-600">Status</th>
                          <th className="text-right px-4 py-2.5 font-medium text-gray-600">Rows</th>
                          <th className="text-right px-4 py-2.5 font-medium text-gray-600 hidden md:table-cell">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {batches.map(b => (
                          <tr key={b.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <p className="text-gray-800 font-medium truncate max-w-[180px]">
                                {b.filename || `Batch #${b.id}`}
                              </p>
                              {b.entity_type && (
                                <span className="text-xs text-gray-400 capitalize">{b.entity_type}s</span>
                              )}
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell">
                              <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                                {b.source_type === 'google_sheets'
                                  ? <><Link size={13} /> Sheets</>
                                  : <><FileSpreadsheet size={13} /> File</>}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${BATCH_STATUS_STYLES[b.status] || BATCH_STATUS_STYLES.pending}`}>
                                {b.status === 'completed' && <CheckCircle size={11} />}
                                {b.status === 'failed' && <XCircle size={11} />}
                                {b.status === 'processing' && <Loader2 size={11} className="animate-spin" />}
                                {b.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">
                              <span className="text-green-600 font-medium">{(b.successful_rows ?? 0).toLocaleString()}</span>
                              {(b.failed_rows ?? 0) > 0 && (
                                <span className="text-amber-600 ml-1.5">/ {b.failed_rows.toLocaleString()} skipped</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-400 text-xs whitespace-nowrap hidden md:table-cell">
                              {formatDate(b.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ════ ORG SETTINGS ════ */}
            {activeTab === 'settings' && (
              <SettingsCard
                icon={Building2}
                title="Organization Settings"
                description="Business rules that drive every AI decision: scoring weights (priority, revenue, proximity, opportunity), distance bands, follow-up cadence, overdue thresholds, and route optimization. Changes apply org-wide; team members can personalize within the limits you set."
                bullets={[
                  'Tier 1 — Critical business rules (contact exclusion, visit duplication, sales cycle)',
                  'Tier 2 — Scoring & prioritization weights',
                  'Tier 3 — Distance & geography bands',
                  'Tier 4 — Route optimization parameters',
                ]}
                buttonLabel="Open Org Settings"
                onOpen={openSettings}
              />
            )}

            {/* ════ SERVICES ════ */}
            {activeTab === 'services' && (
              <SettingsCard
                icon={Package}
                title="Services & Plan"
                description="Manage which NorthAI services are active for your organization, their tiers, and status. Add-ons like Lead Concierge are enabled here."
                bullets={[
                  'GeoAgent — map intelligence, AI briefings, visit planning',
                  'Lead Concierge — AI-powered lead enrichment add-on',
                ]}
                buttonLabel="Open Services"
                onOpen={openServices}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

const STAT_COLORS = {
  blue: 'bg-blue-50 text-blue-600',
  purple: 'bg-purple-50 text-purple-600',
  green: 'bg-green-50 text-green-600',
  amber: 'bg-amber-50 text-amber-600',
};

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className={`inline-flex p-2 rounded-lg mb-2 ${STAT_COLORS[color]}`}>
        <Icon size={16} />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}

function SettingsCard({ icon: Icon, title, description, bullets, buttonLabel, onOpen }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 max-w-2xl">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-blue-50 rounded-xl flex-shrink-0">
          <Icon size={22} className="text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{description}</p>
          <ul className="mt-3 space-y-1.5">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-gray-500">
                <ChevronRight size={13} className="flex-shrink-0 mt-0.5 text-gray-300" />
                {b}
              </li>
            ))}
          </ul>
          <button
            onClick={onOpen}
            className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            {buttonLabel} <ChevronRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
